import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Stepper, Step, StepLabel,
  Card, CardContent, Grid, MenuItem, Chip, CircularProgress,
  InputAdornment, Alert, Divider, Paper, StepConnector, stepConnectorClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import TrainIcon from '@mui/icons-material/Train';
import HotelIcon from '@mui/icons-material/Hotel';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import StarIcon from '@mui/icons-material/Star';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import API from '../api/axios';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: '#eaeaf0',
    borderRadius: 1,
  },
}));

const ColorlibStepIcon = (props) => {
  const { active, completed, icon } = props;
  const icons = {
    1: <FlightTakeoffIcon />,
    2: <PersonIcon />,
    3: <CalendarTodayIcon />,
  };

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: active || completed
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : alpha('#667eea', 0.1),
        color: active || completed ? '#fff' : '#667eea',
        boxShadow: active ? '0 4px 15px rgba(102,126,234,0.4)' : 'none',
        fontSize: '0.85rem',
        fontWeight: 700,
        transition: 'all 0.3s ease',
      }}
    >
      {completed ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : icons[String(icon)]}
    </Box>
  );
};

const BookingForm = ({ tourId, selectedTour, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tourId: tourId || '',
    userName: '',
    email: '',
    phone: '',
    numberOfPeople: 1,
    bookingDate: '',
    serviceType: 'tour',
    mealPreference: 'No Preference',
    specialRequirements: '',
    fromLocation: '',
    toLocation: '',
    travelClass: '',
    returnDate: '',
    checkInDate: '',
    checkOutDate: '',
    roomType: '',
    rooms: 1,
    hotelCity: '',
  });
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = tourId
    ? ['Personal Info', 'Travel Details']
    : ['Select Tour', 'Personal Info', 'Travel Details'];

  const getInitialStep = () => 0;

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleNext = () => {
    const newErrors = {};
    const effectiveStep = tourId ? activeStep + 1 : activeStep;

    if (effectiveStep === 0 && !formData.tourId) {
      newErrors.tourId = 'Please select a tour';
    }
    if (effectiveStep === 1) {
      if (!formData.userName.trim()) newErrors.userName = 'Name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      else if (!/^\+?[1-9]\d{1,14}$/.test(formData.phone.replace(/[\s()-]/g, ''))) {
        newErrors.phone = 'Enter a valid phone number with country code (e.g. +91 9876543210)';
      }
    }
    if (effectiveStep === 2) {
      if (!formData.bookingDate) newErrors.bookingDate = 'Travel date is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const totalSteps = tourId ? 2 : 3;
    if (activeStep === totalSteps - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async () => {
    setSubmitStatus('loading');
    setLoading(true);
    try {
      // Build payload matching server API schema
      const payload = {
        userName: formData.userName,
        email: formData.email,
        phone: formData.phone,
        bookingDate: formData.bookingDate,
        serviceType: 'tour',
        mealPreference: formData.mealPreference,
        tourPackage: formData.tourId ? parseInt(formData.tourId, 10) : null,
        numberOfPeople: parseInt(formData.numberOfPeople, 10) || 1,
        specialRequirements: formData.specialRequirements,
        fromLocation: formData.fromLocation || null,
        toLocation: formData.toLocation || null,
        travelClass: formData.travelClass || null,
        returnDate: formData.returnDate || null,
        checkInDate: formData.checkInDate || null,
        checkOutDate: formData.checkOutDate || null,
        roomType: formData.roomType || null,
        rooms: formData.rooms ? parseInt(formData.rooms, 10) : null,
        hotelCity: formData.hotelCity || null,
      };

      const response = await API.post('/bookings', payload);
      setSubmitStatus('success');

      if (onSuccess) {
        onSuccess(response.data.data || response.data);
      }

      setTimeout(() => {
        setActiveStep(getInitialStep());
        setFormData({
          tourId: tourId || '',
          userName: '', email: '', phone: '', numberOfPeople: 1,
          bookingDate: '', serviceType: 'tour', mealPreference: 'No Preference',
          specialRequirements: '', fromLocation: '', toLocation: '',
          travelClass: '', returnDate: '', checkInDate: '', checkOutDate: '',
          roomType: '', rooms: 1, hotelCity: '',
        });
        setSubmitStatus(null);
        setLoading(false);
      }, 3000);
    } catch (err) {
      setSubmitStatus('error');
      setLoading(false);
    }
  };

  const totalSteps = tourId ? 2 : 3;

  const getStepContent = (step) => {
    const effectiveStep = tourId ? step + 1 : step;

    // Step 0: Tour Selection (only when no tourId is preset)
    if (effectiveStep === 0) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e' }}>
            Selected Tour Package
          </Typography>
          {selectedTour ? (
            <Paper
              sx={{
                p: 2.5,
                cursor: 'pointer',
                borderRadius: 2,
                border: '2px solid #667eea',
                bgcolor: alpha('#667eea', 0.04),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ color: '#667eea', display: 'flex' }}>
                  <FlightTakeoffIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {selectedTour.title}
                  </Typography>
                  {selectedTour.duration && (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: alpha('#667eea', 0.8) }}>
                      {selectedTour.duration} {selectedTour.price ? `• ₹${selectedTour.price?.toLocaleString()}` : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
              {selectedTour.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
                  {selectedTour.description}
                </Typography>
              )}
            </Paper>
          ) : (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No tour selected. Please browse tours to select one.
            </Typography>
          )}
        </Box>
      );
    }

    // Personal Info
    if (effectiveStep === 1) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e' }}>
            Your Contact Information
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.userName}
                onChange={handleChange('userName')}
                error={!!errors.userName}
                helperText={errors.userName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: alpha('#667eea', 0.45), fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: alpha('#667eea', 0.45), fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                placeholder="+91 9876543210"
                value={formData.phone}
                onChange={handleChange('phone')}
                error={!!errors.phone}
                helperText={errors.phone || 'Include country code, e.g. +91'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: alpha('#667eea', 0.45), fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Number of Travelers"
                type="number"
                value={formData.numberOfPeople}
                onChange={handleChange('numberOfPeople')}
                inputProps={{ min: 1, max: 50 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PeopleIcon sx={{ color: alpha('#667eea', 0.45), fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      );
    }

    // Travel Details
    if (effectiveStep === 2) {
      return (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1a1a2e' }}>
            Travel Details
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Travel Date"
                type="date"
                value={formData.bookingDate}
                onChange={handleChange('bookingDate')}
                error={!!errors.bookingDate}
                helperText={errors.bookingDate}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: alpha('#667eea', 0.45), fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Service Type"
                value={formData.serviceType}
                disabled
                helperText="Tour bookings are handled here. Other services are available through the dedicated service sections."
              >
                <MenuItem value="tour">Tour Package</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Meal Preference"
                value={formData.mealPreference}
                onChange={handleChange('mealPreference')}
              >
                <MenuItem value="No Preference">No Preference</MenuItem>
                <MenuItem value="Vegetarian">Vegetarian</MenuItem>
                <MenuItem value="Non-Vegetarian">Non-Vegetarian</MenuItem>
                <MenuItem value="Jain">Jain</MenuItem>
                <MenuItem value="Vegan">Vegan</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Special Requirements / Message"
                value={formData.specialRequirements}
                onChange={handleChange('specialRequirements')}
              />
            </Grid>
            {selectedTour && (
              <Grid item xs={12}>
                <Box sx={{
                  bgcolor: alpha('#667eea', 0.04),
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha('#667eea', 0.12),
                  p: 2,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FlightTakeoffIcon sx={{ fontSize: 20, color: '#667eea' }} />
                    <Typography variant="subtitle2" fontWeight={700} color="#1a1a2e">
                      {selectedTour.title}
                    </Typography>
                    {selectedTour.price && (
                      <Chip
                        label={`₹${selectedTour.price?.toLocaleString()}`}
                        size="small"
                        sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 700, ml: 'auto' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedTour.duration && (
                      <Chip icon={<LocationOnIcon />} label={selectedTour.duration} size="small" variant="outlined" />
                    )}
                    <Chip icon={<PeopleIcon />} label={`${formData.numberOfPeople} traveler${formData.numberOfPeople > 1 ? 's' : ''}`} size="small" variant="outlined" />
                    {formData.bookingDate && (
                      <Chip icon={<CalendarTodayIcon />} label={formData.bookingDate} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      );
    }

    return null;
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto', position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Card elevation={0} sx={{ borderRadius: 4, overflow: 'visible', display: 'flex', flexDirection: 'column', width: '100%', flex: 1, height: '100%' }}>
        <CardContent sx={{ p: { xs: 2, md: 4 }, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
          <Stepper
            activeStep={activeStep}
            connector={<ColorlibConnector />}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={ColorlibStepIcon}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: '#1a1a2e',
                    fontSize: '0.85rem',
                  }}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {getStepContent(activeStep)}

          {submitStatus === 'success' && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography fontWeight={700}>Booking request submitted successfully!</Typography>
              <Typography variant="body2">We'll contact you within 24 hours.</Typography>
            </Alert>
          )}
          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
              Something went wrong. Please try again.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'space-between', mt: 4, pt: 1, alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 } }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              sx={{
                borderRadius: 50,
                px: 4,
                py: 1.2,
                fontWeight: 700,
                textTransform: 'none',
                width: { xs: '100%', sm: 'auto' },
                borderColor: '#0C2340',
                color: '#0C2340',
                '&:hover': { borderWidth: 2, borderColor: '#0C2340', bgcolor: alpha('#0C2340', 0.04) },
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              sx={{
                borderRadius: 50,
                px: 4,
                py: 1.2,
                fontWeight: 700,
                textTransform: 'none',
                width: { xs: '100%', sm: 'auto' },
                background: 'linear-gradient(135deg, #0C2340 0%, #183B63 100%)',
                boxShadow: '0 8px 25px rgba(12,35,64,0.3)',
                '&:disabled': {
                  background: alpha('#0C2340', 0.3),
                  boxShadow: 'none',
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #183B63 0%, #0C2340 100%)',
                },
              }}
              endIcon={activeStep === totalSteps - 1 ? <SendIcon /> : null}
            >
              {activeStep === totalSteps - 1
                ? (loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Submit Booking')
                : 'Continue'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BookingForm;